import { AxiosHttpClient } from '@antecipa/core/http/HttpClients';
import { HttpResponse } from '@antecipa/core/http/interfaces';
import { BearerToken } from '@antecipa/core/models/auth/token';

import { ChangePasswordParam } from 'domain/models/auth/change-password.param';
import { ConfirmRecoveryPasswordParam } from 'domain/models/auth/confirm-recovery-password.param';
import { CorporateSignInParameters } from 'domain/models/auth/CorporateSignInParameters';
import { RespondToAuthChallengeParam } from 'domain/models/auth/respond-to-auth-challenge.param';
import { SigninParam } from 'domain/models/auth/signin.param';
import { SignupConfirmParam } from 'domain/models/auth/signup-confirm.param';
import { CorporateLoginHandler } from './corporateLoginHandler';

export const AuthApiService = () => {
  const client = AxiosHttpClient(import.meta.env.VITE_API_AUTH);

  const handleRecaptchaToken = (_recaptchaToken) => {
    if (import.meta.env.VITE_APPLICATION_ENV === 'localhost') {
      return import.meta.env.VITE_RECAPTCHA_KEY_V3_TOKEN;
    } else {
      return _recaptchaToken;
    }
  };

  function signUp(data: SigninParam, recaptchaToken) {
    return client.makeRequest({
      url: '/auth/signup',
      method: 'POST',
      data,
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  function signUpConfirm(data: SignupConfirmParam, recaptchaToken) {
    return client.makeRequest({
      url: '/auth/signupconfirm',
      method: 'PATCH',
      data,
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  async function signIn({ username, password }, recaptchaToken) {
    const response = await client.makeRequest({
      url: '/auth/signin',
      method: 'POST',
      data: {
        username,
        password,
      },
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
    return response.data;
  }

  function signInOtp(username: string, recaptchaToken) {
    return client
      .makeRequest<any>({
        url: '/auth/signinotp',
        method: 'POST',
        data: {
          username,
        },
        headers: {
          'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
        },
      })
      .then((response) => response.data);
  }

  function signOut() {
    return client.makeRequest({
      url: '/auth/signout',
      method: 'POST',
      data: {},
    });
  }

  function getRole(roleId) {
    return client.makeRequest({
      url: `/auth/roles/${roleId}`,
      method: 'GET',
    });
  }

  function getRoles() {
    return client.makeRequest({
      url: '/auth/roles',
      method: 'GET',
    });
  }

  function goToCorporateLogin(recaptchaToken, email): Promise<any> {
    return client
      .makeRequest({
        url: `/Auth/SamlProvider/${email}`,
        method: 'GET',
        headers: {
          'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
        },
      })
      .then((response) => CorporateLoginHandler(response));
  }

  function refreshToken(token) {
    return client.makeRequest({
      url: '/auth/refreshtoken',
      method: 'POST',
      data: {
        refreshToken: token,
      },
    });
  }

  function respondToAuthChallenge(
    data: RespondToAuthChallengeParam,
    recaptchaToken
  ) {
    return client
      .makeRequest({
        url: '/auth/respondtoauthchallenge',
        method: 'POST',
        data,
        headers: {
          'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
        },
      })
      .then((response) => response.data);
  }

  function resendConfirmationCode(username: string, recaptchaToken) {
    return client.makeRequest({
      url: '/auth/resendconfirmationcode',
      method: 'POST',
      data: {
        username,
      },
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  function confirmForgotPassword(
    data: ConfirmRecoveryPasswordParam,
    recaptchaToken
  ) {
    return client.makeRequest({
      url: '/auth/confirmforgotpassword',
      method: 'PATCH',
      data,
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  function changePassword(data: ChangePasswordParam, recaptchaToken) {
    return client.makeRequest({
      url: '/auth/changepassword',
      method: 'PATCH',
      data,
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  function corporateSignIn(
    data: CorporateSignInParameters,
    recaptchaToken: string
  ) {
    return client.makeRequest<Promise<HttpResponse<BearerToken>>>({
      url: '/auth/signincode',
      method: 'POST',
      data,
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  function forgotPassword(username: string, recaptchaToken) {
    return client.makeRequest({
      url: '/auth/forgotpassword',
      method: 'POST',
      data: {
        username,
      },
      headers: {
        'g-recaptcha-response': handleRecaptchaToken(recaptchaToken),
      },
    });
  }

  function verifyToken(): Promise<boolean> {
    return client
      .makeRequest({ url: '/auth/verifytoken', method: 'POST', data: {}, shouldRetry: false })
      .then((response) => response.status === 200)
      .catch(() => false);
  }

  return {
    signUp,
    signUpConfirm,
    signIn,
    signInOtp,
    signOut,
    getRole,
    getRoles,
    refreshToken,
    respondToAuthChallenge,
    resendConfirmationCode,
    confirmForgotPassword,
    changePassword,
    forgotPassword,
    goToCorporateLogin,
    corporateSignIn,
    verifyToken,
  };
};
